import {
  DetailsQuestion,
  DetailsArrayQuestion,
  DetailFieldConfig,
} from '../../types/form';

type FormAnswers = Record<string, unknown>;

/**
 * resolveVisibleFields — the FIELD-LEVEL `showIf` rule for details / details-array
 * questions, extracted as one pure function (ADR-001 §2.3, folds-in item 8 — field
 * `showIf`). It reproduces, exactly, the visibility decision DetailsFieldsRenderer
 * previously made inline (the old `.filter(...)` + its `getValue`), and is now the
 * single source DetailsFieldsRenderer uses for both the rendered `visibleFields` and
 * the hidden-field reset effect.
 *
 * The rule (including the details vs details-array LOOKUP DIVERGENCE —
 * 02-current-state-map.md §4 mechanism 8):
 *   - A field WITHOUT `showIf` is always visible.
 *   - A field WITH `showIf: { field, equals }` is visible iff the sibling value equals
 *     `equals`. The sibling is looked up differently per question type:
 *       • details        → answers[question.id]?.[showIf.field]  (nested by fieldId)
 *       • details-array   → answers[showIf.field]                (TOP-LEVEL, not per-row)
 *     This divergence is intentional and matches DetailsFieldsRenderer today.
 */

export interface ResolvedField {
  fieldId: string;
  field: DetailFieldConfig;
  visible: boolean;
}

/**
 * Returns every real field id exactly once in its presentation order.
 *
 * A partially-edited fieldOrder is tolerated deliberately: unknown/duplicate ids
 * are ignored and real fields omitted from the array are appended in the legacy
 * Object.keys order. Forms without fieldOrder therefore render exactly as before.
 */
export function resolveFieldOrder(
  question: DetailsQuestion | DetailsArrayQuestion,
): string[] {
  const fields = question.fields || {};
  const configured = Array.isArray(question.fieldOrder)
    ? question.fieldOrder
    : [];
  const seen = new Set<string>();
  const ordered: string[] = [];

  for (const fieldId of configured) {
    if (
      typeof fieldId === 'string' &&
      Object.prototype.hasOwnProperty.call(fields, fieldId) &&
      !seen.has(fieldId)
    ) {
      seen.add(fieldId);
      ordered.push(fieldId);
    }
  }
  for (const fieldId of Object.keys(fields)) {
    if (!seen.has(fieldId)) ordered.push(fieldId);
  }
  return ordered;
}

/**
 * Reads the sibling value that a field's `showIf` condition compares against,
 * mirroring DetailsFieldsRenderer's former inline getValue.
 */
function getShowIfSiblingValue(
  question: DetailsQuestion | DetailsArrayQuestion,
  field: DetailFieldConfig,
  answers: FormAnswers,
): unknown {
  if (!field.showIf) return undefined;
  const depField = field.showIf.field;
  if (question.type === 'details-array') {
    return answers[depField];
  }
  const nested = answers[question.id];
  return typeof nested === 'object' && nested !== null
    ? (nested as Record<string, unknown>)[depField]
    : undefined;
}

/**
 * Resolves field-level `showIf` visibility for every field of a details /
 * details-array question. Returns one entry per field in definition order with
 * `{ fieldId, field, visible }`. Pure: no mutation, no side effects.
 */
export function resolveVisibleFields(
  question: DetailsQuestion | DetailsArrayQuestion,
  answers: FormAnswers,
): ResolvedField[] {
  const fields = question.fields || {};
  return resolveFieldOrder(question).map((fieldId) => {
    const field = fields[fieldId];
    if (!field.showIf) {
      return { fieldId, field, visible: true };
    }
    const { equals } = field.showIf;
    const value = getShowIfSiblingValue(question, field, answers);
    return { fieldId, field, visible: value === equals };
  });
}

export default resolveVisibleFields;
